<script setup>
import { Head, Link, useForm, usePage } from "@inertiajs/vue3";
import AuthenticationCard from "@/Components/AuthenticationCard.vue";
import AuthenticationCardLogo from "@/Components/AuthenticationCardLogo.vue";
import Checkbox from "@/Components/Checkbox.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SimpleSelect from "@/Components/SimpleSelect.vue";
import TextInput from "@/Components/TextInput.vue";
import { vMaska } from "maska";
import { computed, ref } from "vue";

const props = defineProps({
    partner: Object,
})

const message = computed(() => usePage().props?.flash?.message)
const countries = computed(() => usePage().props?.countries)
const partner = computed(() => usePage().props?.partner)

const inputName = ref();

const form = useForm({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    terms: false,
    country: "us",
});
const isDisabled = computed(() => form.country !== "us");

const submit = () => {
    if(isDisabled.value === true) return;

    const registerUrl = partner.value
        ? route("register", { partner: partner.value.slug })
        : route("register.non-partner");
    form.post(registerUrl, {
        onFinish: () => {
            form.reset("password", "password_confirmation");
        },
        onSuccess: () => {
            form.reset();
            inputName.value.focus();
        },
    });
};
</script>

<template>
    <Head title="Register" />

    <AuthenticationCard>
        <template #logo>
            <AuthenticationCardLogo />
        </template>

        <template #afterLogo v-if="message || isDisabled">
            <div v-if="message" class="font-medium text-sm text-allday-dark-700">
                {{ message }}
            </div>
            <div v-if="isDisabled" class="font-medium text-sm text-red-600 text-center">
                We're not currently available in your country. <br/> <a href="https://alldayordering.com/international-waiting-list" target="_blank" class="underline text-allday-dark-700">Join our waitlist</a> to be notified when we are.
            </div>
        </template>

        <form @submit.prevent="submit">

            <div
                class="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-4"
            >
                <div class="col-span-2">
                    <InputLabel class="text-white" for="name" value="Name" />
                    <TextInput
                        id="name"
                        v-model="form.name"
                        type="text"
                        class="mt-1 block w-full"
                        required
                        autofocus
                        autocomplete="name"
                        ref="inputName"
                        :disabled="isDisabled"
                    />
                    <InputError class="mt-2" :message="form.errors.name" />
                </div>

                <div class="col-span-2">
                    <InputLabel class="text-white" for="email" value="Email" />
                    <TextInput
                        id="email"
                        v-model="form.email"
                        type="email"
                        class="mt-1 block w-full"
                        required
                        autocomplete="username"
                        :disabled="isDisabled"
                    />
                    <InputError class="mt-2" :message="form.errors.email" />
                </div>

                <div class="col-span-2">
                    <InputLabel
                        class="text-white"
                        for="password"
                        value="Password"
                    />
                    <TextInput
                        id="password"
                        v-model="form.password"
                        type="password"
                        class="mt-1 block w-full"
                        required
                        autocomplete="new-password"
                        :disabled="isDisabled"
                    />
                    <p class="text-xs mt-2 text-white italic">
                        Your password must be at least 10 characters long and
                        include an uppercase letter, a number, and a special
                        symbol.
                    </p>
                    <InputError class="mt-2" :message="form.errors.password" />
                </div>

                <div class="col-span-2">
                    <InputLabel
                        class="text-white"
                        for="password_confirmation"
                        value="Confirm Password"
                    />
                    <TextInput
                        id="password_confirmation"
                        v-model="form.password_confirmation"
                        type="password"
                        class="mt-1 block w-full"
                        required
                        autocomplete="new-password"
                        :disabled="isDisabled"
                    />
                    <InputError
                        class="mt-2"
                        :message="form.errors.password_confirmation"
                    />
                </div>

                <div class="col-span-2">
                    <InputLabel
                        class="text-white"
                        for="country"
                        value="Country"
                    />
                    <SimpleSelect v-model="form.country" id="country">
                        <option v-for="country in countries" :value="country.value" :key="`country-option-${country.value}`">{{ country.label }}</option>
                    </SimpleSelect>
                    <InputError
                        class="mt-2"
                        :message="form.errors.country"
                    />
                </div>
            </div>

            <div>
                <div
                    v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature"
                    class="mt-4"
                >
                    <InputLabel class="text-white" for="terms">
                        <div class="flex items-center">
                            <Checkbox
                                id="terms"
                                v-model:checked="form.terms"
                                name="terms"
                                required
                            />

                            <div class="ml-2">
                                I agree to the
                                <a
                                    target="_blank"
                                    href="https://alldayordering.com/terms-of-service/"
                                    class="underline text-sm text-allday-dark-50 hover:text-allday-dark-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-allday-dark-500"
                                    >Terms of Service</a
                                >
                                and
                                <a
                                    target="_blank"
                                    href="https://alldayordering.com/privacy-policy/"
                                    class="underline text-sm text-allday-dark-50 hover:text-allday-dark-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-allday-dark-500"
                                    >Privacy Policy</a
                                >
                            </div>
                        </div>
                        <InputError class="mt-2" :message="form.errors.terms" />
                    </InputLabel>
                </div>
                <div class="flex items-center justify-end mt-8">
                    <PrimaryButton
                        class="flex justify-center w-full h-[48px]"
                        :class="{ 'opacity-25': form.processing }"
                        :disabled="form.processing || isDisabled"
                    >
                        Create Account
                    </PrimaryButton>
                </div>
            </div>
        </form>

        <template #footer>
            <div class="text-center">
                <Link
                    :href="route('login')"
                    class="underline text-sm text-allday-dark-800 hover:text-allday-dark-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-allday-dark-500"
                >
                    Already registered? Click here to Login
                </Link>
            </div>
        </template>
    </AuthenticationCard>
</template>
